import { t } from "i18next";
import { Pencil, Trash2 } from "lucide-react";
import { formatDate } from "../../utils/utils";
import { htmlToPlainText } from "../../utils/richText";

export type NoticeRow = {
  id: number;
  title: string;
  content: string;
  user?: { id: number; userId: string; name: string };
  create_at: string;
  update_at: string;
};

type Props = {
  items: NoticeRow[];
  page: number;
  pageSize?: number;
  /** 수정/삭제 버튼 노출 여부. 백엔드 권한(관리자 + 작성자 본인)과 같은 조건을 넘긴다 */
  canEdit: (row: NoticeRow) => boolean;
  canDelete: (row: NoticeRow) => boolean;
  onRowClick: (row: NoticeRow) => void;
  onEdit: (row: NoticeRow) => void;
  onDelete: (row: NoticeRow) => void;
};

export default function NoticeTable({ items, page, pageSize = 10, canEdit, canDelete, onRowClick, onEdit, onDelete }: Props) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full table-fixed text-sm">
        <colgroup>
          <col style={{ width: 70 }} />
          <col style={{ width: "26%" }} />
          <col />
          <col style={{ width: 130 }} />
          <col style={{ width: 180 }} />
          <col style={{ width: 90 }} />
        </colgroup>

        <thead className="bg-gray-50 text-gray-600">
          <tr>
            <th className="px-4 py-3 text-left font-semibold">No.</th>
            <th className="px-4 py-3 text-left font-semibold">{t("NoticeTitle")}</th>
            <th className="px-4 py-3 text-left font-semibold">{t("NoticeContent")}</th>
            <th className="px-4 py-3 text-left font-semibold">{t("Author")}</th>
            <th className="px-4 py-3 text-left font-semibold">{t("UpdatedAt")}</th>
            <th className="px-4 py-3" />
          </tr>
        </thead>

        <tbody>
          {items.map((item, index) => (
            <tr key={item.id} onClick={() => onRowClick(item)} className="border-t hover:bg-gray-50 transition cursor-pointer">
              <td className="px-4 py-3 font-medium text-gray-900">{(page - 1) * pageSize + index + 1}</td>
              <td className="px-4 py-3">
                <div className="truncate font-medium text-gray-900">{item.title}</div>
              </td>
              <td className="px-4 py-3">
                {/* 본문은 HTML 이라 목록에서는 태그를 걷어낸 텍스트만 보여준다 */}
                <div className="truncate text-gray-500">{htmlToPlainText(item.content)}</div>
              </td>
              <td className="px-4 py-3 text-gray-600">{item.user?.name ?? "-"}</td>
              <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{formatDate(item.update_at)}</td>
              <td className="px-4 py-3">
                <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                  {canEdit(item) && (
                    <button
                      type="button"
                      title={t("Edit")}
                      onClick={() => onEdit(item)}
                      className="p-1.5 rounded-md text-gray-400 hover:text-blue-500 hover:bg-gray-100 transition"
                    >
                      <Pencil size={15} />
                    </button>
                  )}
                  {canDelete(item) && (
                    <button
                      type="button"
                      title={t("Delete")}
                      onClick={() => onDelete(item)}
                      className="p-1.5 rounded-md text-gray-400 hover:text-red-500 hover:bg-gray-100 transition"
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
